/**
 * The retailer free-delivery nudge: "you qualify" once the cart reaches the threshold, "order for
 * X or more" while it is short of it.
 *
 * Rendered from CartSummary, which puts it on both surfaces where a retailer can still act on it:
 * the cart page and the quick order tab, which reuses that same summary. Deliberately NOT on
 * checkout — by then the order is settled, and "add more to qualify" is advice too late to take.
 *
 * Ported from Webshop-frontend's FreeDeliveryBanner so both storefronts say the same thing under
 * the same rule. The copy is authored on the checkout page in Umbraco (not the dictionary) and
 * reaches us on the cart, so there is one source of wording for the two shops.
 *
 * Informational only: it does not discount the real shipping charge, and B2B carts hide the
 * delivery row entirely.
 */

import { useMemo } from "react";
import type { Cart } from "@/types/cart";
import { FreeDeliveryIcon } from "@/components/icons";
import styles from "./FreeDeliveryNotice.module.css";

interface FreeDeliveryNoticeProps {
  cart: Cart | null;
}

export function FreeDeliveryNotice({ cart }: FreeDeliveryNoticeProps) {
  const notice = useMemo(() => {
    const freeDelivery = cart?.freeDelivery;
    if (!cart || cart.lines.length === 0 || !freeDelivery) {
      return null;
    }

    // Threshold 0 means "no threshold configured", not "everything qualifies" — matching
    // Webshop-frontend, where a falsy threshold hides the banner.
    const { threshold, formattedThreshold } = freeDelivery;
    if (!threshold || !formattedThreshold) {
      return null;
    }

    // Line totals rather than the cart total: shipping and order-level adjustments are not part
    // of what earns free delivery. Same sum as the other storefront.
    const cartTotal = cart.lines.reduce(
      (sum, line) => sum + line.totalPrice,
      0
    );
    const isEligible = cartTotal >= threshold;

    const message = isEligible
      ? freeDelivery.aboveThresholdMessage
      : freeDelivery.belowThresholdMessage;

    // A culture with no copy authored shows nothing rather than an empty banner.
    if (!message) {
      return null;
    }

    return {
      text: message.replace("{threshold}", formattedThreshold),
      isEligible,
    };
  }, [cart]);

  if (!notice) {
    return null;
  }

  return (
    <div
      className={`${styles.notice} ${notice.isEligible ? styles.eligible : styles.belowThreshold}`}
      role="status"
    >
      <FreeDeliveryIcon
        className={styles.icon}
        aria-hidden="true"
        focusable="false"
      />
      <p className={styles.text}>{notice.text}</p>
    </div>
  );
}

export default FreeDeliveryNotice;
